/**
 * Staff caller scope (#36196, #36202).
 *
 * The staff agency claims (`custom:affinity_groups`, `custom:affinity_group_regex`,
 * `custom:allowed_states`, `custom:unlicensed_states`) are writable by the staff app client (the Descope
 * IdP mapping requires it), and AppSync also accepts the pool's access token, which carries none of
 * them. So "no affinity claims" can't by itself mean internal staff.
 *
 * StaffPreTokenGenerationLambda stamps the Descope values from the user's staffuserpermissions record
 * onto the ID token and adds AGENCY_CLAIMS_MARKER_CLAIM, which is not a Cognito attribute and so can't
 * be written by the user. (In a Descope-off personal dev env it stamps the user's own attributes; see
 * the staff-appsync-be README.) Every staff gate that tells internal staff from agents trusts the
 * agency claims only on such a token. Anything else is an agent whose book can't be verified, and
 * gates fail closed.
 */

/** Added by StaffPreTokenGenerationLambda only alongside the Descope agency claims. */
export const AGENCY_CLAIMS_MARKER_CLAIM = 'branch:agency_claims';
export const AGENCY_CLAIMS_MARKER_VALUE = 'descope';

export interface StaffCallerIdentity {
  claims?: Record<string, unknown> | null;
}

export interface StaffCallerScope {
  /** An ID token carrying the marker: its agency claims are the Descope values. */
  verified: boolean;
  /** Has an affinity scope, or unverified or unparsable affinity claims. Treat as an agent. */
  isExternalAgent: boolean;
  /** Unverified, or any claim unparsable: an AGENT's book can't be determined, so grant them nothing scoped. */
  claimsMalformed: boolean;
  affinityCodes: string[];
  affinityStems: string[];
  /** allowed_states + unlicensed_states. */
  viewableStates: string[];
}

// Absent, '', JSON null and [] mean "none". Anything else that isn't an array of strings is malformed:
// treating e.g. '"ABC1"' or '[123]' as "none" would turn an agent into internal staff.
function parseClaimArray(raw: unknown): { ok: boolean; values: string[] } {
  if (raw == null || raw === '') {
    return { ok: true, values: [] };
  }
  if (typeof raw !== 'string') {
    return { ok: false, values: [] };
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return { ok: false, values: [] };
  }
  if (value === null) {
    return { ok: true, values: [] };
  }
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    return { ok: false, values: [] };
  }
  return { ok: true, values: value as string[] };
}

export function getStaffCallerScope(identity: StaffCallerIdentity | null | undefined): StaffCallerScope {
  const claims = identity?.claims ?? {};
  const verified = claims.token_use === 'id' && claims[AGENCY_CLAIMS_MARKER_CLAIM] === AGENCY_CLAIMS_MARKER_VALUE;
  const codes = parseClaimArray(claims['custom:affinity_groups']);
  const stems = parseClaimArray(claims['custom:affinity_group_regex']);
  const allowedStates = parseClaimArray(claims['custom:allowed_states']);
  const unlicensedStates = parseClaimArray(claims['custom:unlicensed_states']);

  const claimsMalformed = !verified || !codes.ok || !stems.ok || !allowedStates.ok || !unlicensedStates.ok;
  return {
    verified,
    // Only unverified or unparsable AFFINITY claims make a caller an agent. A malformed state list
    // (Descope keeps legacy staff states as-is) leaves an internal user internal, as before; it only
    // makes their state scope unusable (claimsMalformed).
    isExternalAgent: !verified || !codes.ok || !stems.ok || codes.values.length > 0 || stems.values.length > 0,
    claimsMalformed,
    affinityCodes: codes.values,
    affinityStems: stems.values,
    viewableStates: [...allowedStates.values, ...unlicensedStates.values]
  };
}

/** A verified ID token with no affinity scope. */
export function isVerifiedInternalStaff(identity: StaffCallerIdentity | null | undefined): boolean {
  return !getStaffCallerScope(identity).isExternalAgent;
}

/**
 * Whether a record is in the caller's book: one of its states must be viewable, and when it has an
 * affinity, the code or its stem must be one the caller holds. The same rule the getAccount response
 * resolver, get-offer and the document gates apply. The caller passes the stem (`affinityGroupStem`,
 * else `affinityToStem(affinity)` from @ourbranch/policy-utils) so this package stays dependency-free.
 */
export function recordInStaffCallerScope(
  scope: Pick<StaffCallerScope, 'affinityCodes' | 'affinityStems' | 'viewableStates'>,
  { states, affinity, affinityStem }: { states: string[]; affinity: string; affinityStem: string }
): boolean {
  if (!states.some((state) => scope.viewableStates.includes(state))) {
    return false;
  }
  if (!affinity) {
    return true;
  }
  return scope.affinityCodes.includes(affinity) || scope.affinityStems.includes(affinityStem);
}
